import React, { Fragment, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Card } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useStateMachine } from "little-state-machine";
import PropTypes from "prop-types";
import moment from "moment";

import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import errorToast from "helpers/errorToast";
import RateNowDialog from "pages/common/ShipmentBilling/components/RateNowDialog/RateNowDialog";
import Star from "pages/common/ShipmentBilling/components/Star/Star";
import usePermissions from "hooks/usePermissions";
import Button from "components/atoms/Button/Button";
import testIds from "constants/testIds";
import { updateShipmentId } from "store/store";
import { useGetRecentlyDelivered } from "api/queries/user/useGetRecentlyDelivered";

const RecentDeliveriesWidget = function ({ isLayoutLocked, isPreview, paymentLocation, widgetId, handleWidgetDialogOpen }) {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const intl = useIntl();
  const { actions } = useStateMachine({ updateShipmentId });
  const paymentLocations = JSON.parse(permissions)?.payment_location?.locations;
  const { user_type } = JSON.parse(permissions);
  const paymentLocationLabel = paymentLocations?.find((pl) => pl.value === paymentLocation)?.label;

  const [showSubmitPodDialog, setShowSubmitPodDialog] = useState(false);
  const [shipmentId, setShipmentId] = useState("");

  const { data: recentlyDeliveredShipments, isFetching } = useGetRecentlyDelivered(
    {
      onError: (err) => {
        errorToast(
          err,
          intl.formatMessage({
            id: "dashboard.recentDeliveriesWidget.couldNotRetrieveRecentDelivery",
            defaultMessage: "Could not retrieve recently delivered shipments. Please try again later.",
          }),
        );
      },
    },
    paymentLocation !== "All" ? paymentLocation : null,
  );

  const handleRateDialog = (shipmentId) => {
    setShipmentId(shipmentId);
    setShowSubmitPodDialog(true);
  };

  if (isFetching) {
    return (
      <div className="overflow-hidden">
        <LoadingSkeleton
          header={intl.formatMessage({
            id: "dashboard.recentDeliveries",
            defaultMessage: "Recent deliveries",
          })}
          rows={isPreview ? 2 : 6}
          className="pl-16 pr-16 pt-1"
        />
      </div>
    );
  }
  return (
    <>
      <RateNowDialog
        id={shipmentId}
        isOpen={showSubmitPodDialog}
        close={() => {
          setShowSubmitPodDialog(false);
        }}
      />
      <Card.Body className="overflow-auto">
        <div
          className={`d-flex justify-content-${
            isLayoutLocked ? "center" : "between"
          } align-items-center pb-2 br-5 bg-white rounded-bottom-left-0 rounded-bottom-right-0`}
        >
          <h5 className="d-flex align-items-center">
            <FormattedMessage id="dashboard.recentDeliveries" defaultMessage="Recent deliveries" />
            {paymentLocation && paymentLocation !== "All" && (
              <span className="font-b2 text-secondary ml-2">({paymentLocationLabel})</span>
            )}
          </h5>
          {!isLayoutLocked && !isPreview && (
            <Button
              className="edit-btn"
              onClick={() => {
                handleWidgetDialogOpen(widgetId);
              }}
              data-testid={`${testIds.EDIT_WIDGET_BUTTON}-${widgetId}`}
            >
              <FormattedMessage id="general.edit" defaultMessage="Edit" />
            </Button>
          )}
        </div>
        {!recentlyDeliveredShipments?.length && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "80%" }}>
            <span className="text-secondary text-center">
              No recent deliveries {paymentLocationLabel && <>for {paymentLocationLabel}</>}
            </span>
          </div>
        )}
        <ul className="row flex-column list-widget-container">
          {recentlyDeliveredShipments?.length &&
            recentlyDeliveredShipments
              .slice(0, isPreview ? 2 : recentlyDeliveredShipments?.length)
              .map(({ overall_rating, delivered_by, reference, delivery_city, delivery_country_code, time_of_delivery }) => {
                const currentYear = moment().year();
                const isCurrentYear = moment(time_of_delivery).year() === currentYear;
                const date = isCurrentYear
                  ? moment(time_of_delivery).format("MMM DD")
                  : moment(time_of_delivery).format("MMM DD, YYYY");
                const time = moment(time_of_delivery).format("HH:mm");
                return (
                  <Fragment key={reference + delivered_by}>
                    <li
                      className={`col border-bottom star-main-li border-grey7 list-widget-item ${
                        !isLayoutLocked || isPreview ? "previewing" : ""
                      }`}
                      aria-hidden
                      onClick={() => {
                        if (isLayoutLocked && !isPreview) {
                          setShipmentId(reference);
                          navigate(`/shipment/${reference}/tracking`);
                          actions.updateShipmentId({
                            shipmentId: reference,
                          });
                        }
                      }}
                    >
                      <div className="text-primary pt-4">
                        <div className="d-flex justify-content-between align-items-center pb-1 mb-1">
                          <span className="font-b4">{reference}</span>
                          {!isPreview &&
                            user_type === "shipper" &&
                            (overall_rating ? (
                              <Link
                                to="#"
                                className="inherit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRateDialog(reference);
                                }}
                              >
                                <Star color="#FDB833" count={overall_rating} size="sm" />
                              </Link>
                            ) : (
                              <Button
                                variant="primary"
                                className="rounded-pill rate-btn"
                                onClick={(e) => {
                                  setShipmentId(reference);
                                  e.stopPropagation();
                                  handleRateDialog(reference);
                                }}
                              >
                                <FormattedMessage id="billing.rate" defaultMessage="Rate" />
                              </Button>
                            ))}
                        </div>
                      </div>
                      <div className="pb-4 font-b3 d-flex flex-column">
                        <span>
                          <FormattedMessage id="dashboard.deliveredOn" defaultMessage="Delivered on" /> {date} - {time}
                        </span>
                        <span>
                          <FormattedMessage id="general.to" defaultMessage="to" /> {delivery_city}, {delivery_country_code}
                        </span>
                        <span>
                          <FormattedMessage id="general.by" defaultMessage="by" /> {delivered_by}
                        </span>
                      </div>
                    </li>
                    <hr className="list-widget-line" />
                  </Fragment>
                );
              })}
        </ul>
      </Card.Body>
    </>
  );
};

RecentDeliveriesWidget.propTypes = {
  paymentLocation: PropTypes.string,
  isLayoutLocked: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool,
  widgetId: PropTypes.number,
  handleWidgetDialogOpen: PropTypes.func,
};

RecentDeliveriesWidget.defaultProps = {
  paymentLocation: null,
  isPreview: false,
  widgetId: null,
  handleWidgetDialogOpen: () => {},
};

export default RecentDeliveriesWidget;
